import { BizAppConfig } from 'config/application.manifest';

const YEEAUTH_ORIGIN = 'https://yeeauth.luoleshangcai.com';
const SCHOOL_APP_ID = '992c651771ba49509816e4b92b2d7b27';
const LOGIN_FORWARD_QUERY_KEYS = [
  'app_id',
  'redirect_uri',
  'redirect',
  'logout',
  'state',
  'scope',
  'response_type',
] as const;

type SearchInput = string | URLSearchParams;

const normalizeQueryValue = (value: string | null | undefined): string => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
    return '';
  }
  return trimmed;
};

const toSearchParams = (input: SearchInput): URLSearchParams =>
  input instanceof URLSearchParams ? input : new URLSearchParams(input);

export const buildAuthPathWithQuery = (
  pathname: string,
  search: SearchInput,
  extraParams?: Record<string, string>,
): string => {
  const sourceParams = toSearchParams(search);
  const nextParams = new URLSearchParams();

  for (const key of LOGIN_FORWARD_QUERY_KEYS) {
    const value = normalizeQueryValue(sourceParams.get(key));
    if (value) {
      nextParams.set(key, value);
    }
  }

  for (const [key, value] of Object.entries(extraParams ?? {})) {
    const normalized = normalizeQueryValue(value);
    if (normalized) {
      nextParams.set(key, normalized);
    }
  }

  const query = nextParams.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
};

export const buildLoginPathWithQuery = (search: string): string => {
  const sourceParams = toSearchParams(search);
  const loginPath =
    normalizeQueryValue(sourceParams.get('app_id')) === SCHOOL_APP_ID
      ? '/school-user/login'
      : '/user/login';

  return buildAuthPathWithQuery(loginPath, sourceParams);
};

export const hasLoginRelayQuery = (search: SearchInput): boolean => {
  const searchParams = toSearchParams(search);

  return LOGIN_FORWARD_QUERY_KEYS.some((key) =>
    Boolean(normalizeQueryValue(searchParams.get(key))),
  );
};

/**
 * 校验 yeeauth 中心认证服务器的会话状态（参考 yeeauth-portal）。
 * SSO 登录后 yeeauth 域名下会有会话 cookie，此接口返回用户信息；
 * 本域 localStorage 里没有 token，不能作为登录态依据。
 */
export const fetchYeeauthUserInfo = async (): Promise<Record<
  string,
  unknown
> | null> => {
  try {
    const res = await fetch(`${YEEAUTH_ORIGIN}/userinfo`, {
      credentials: 'include',
    });
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    if (data && (data.success || data.id || data.sub)) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
};

const DEFAULT_APP_ID = BizAppConfig.defaultSsoAppId;

const getDefaultRedirectUri = (): string => {
  return BizAppConfig.defaultSsoRedirectUri;
};

/**
 * 确保 app_id 和 redirect_uri 有值，缺失时用环境变量和当前域名填充默认值。
 */
export const ensureLoginParams = (params: URLSearchParams): URLSearchParams => {
  const result = new URLSearchParams(params);

  if (!normalizeQueryValue(result.get('app_id'))) {
    result.set('app_id', DEFAULT_APP_ID);
  }
  if (!normalizeQueryValue(result.get('redirect_uri'))) {
    result.set('redirect_uri', getDefaultRedirectUri());
  }

  return result;
};

/**
 * 从登录接口响应里取 token。真实返回形状不稳定（可能是 {token} 或
 * {access_token}，也可能包在 data/result 里），逐个候选字段尝试。
 */
export const resolveLoginToken = (payload: unknown): string => {
  const candidates = [
    payload,
    (payload as { data?: unknown } | null | undefined)?.data,
    (payload as { result?: unknown } | null | undefined)?.result,
  ];
  for (const candidate of candidates) {
    const item = candidate as
      | { token?: unknown; access_token?: unknown }
      | null
      | undefined;
    const token =
      normalizeQueryValue(item?.token as string | undefined) ||
      normalizeQueryValue(item?.access_token as string | undefined);
    if (token) {
      return token;
    }
  }
  return '';
};

export const parseOidcParams = (
  search: SearchInput,
): { app_id: string; redirect_uri: string; state: string } => {
  const searchParams = toSearchParams(search);
  const get = (key: string) => normalizeQueryValue(searchParams.get(key));

  // 未传 app_id/redirect_uri 时，从环境变量和当前域名取默认值
  const params = ensureLoginParams(searchParams);
  return {
    app_id: get('app_id') || params.get('app_id') || '',
    redirect_uri: get('redirect_uri') || params.get('redirect_uri') || '',
    state: get('state'),
  };
};

/**
 * 用隐藏表单把 token POST 到 yeeauth 的 OIDC 端点，由它建立会话并跳回业务系统。
 */
export const runSsoBridge = (
  rawToken: unknown,
  search: SearchInput,
): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const token = normalizeQueryValue(rawToken as string | undefined);
  if (!token) {
    return false;
  }

  const oidcParams = parseOidcParams(search);

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${YEEAUTH_ORIGIN}/oauth/oidc/login`;
  form.style.display = 'none';

  const tokenInput = document.createElement('input');
  tokenInput.name = 'token';
  tokenInput.value = token;
  form.appendChild(tokenInput);

  for (const [key, value] of Object.entries(oidcParams)) {
    if (value) {
      const input = document.createElement('input');
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }
  }

  document.body.appendChild(form);
  form.submit();
  return true;
};
